import {
  useMutationKisiGenerateSignatureKey,
  useQueryKisiBrandConfig,
} from "@gymflow/api";
import { atom, useAtom } from "jotai";
import { AnimatePresence, motion } from "motion/react";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ModalContext, ModalWrapper } from "../../../../providers";
import useGymflowModels from "../../../../store";
import {
  Button,
  CheckIcon,
  CopyIcon,
  CopyInput,
  DangerIcon,
  ReactiveButton,
  RefreshIcon,
} from "../../../atoms";
import { PillTabs } from "../../../atoms/base/PillTabs";
import { ArrowUpRightIcon } from "../../../atoms/icons/ArrowUpRightIcon";
import { InfoCircleIcon } from "../../../atoms/icons/InfoCircleIcon";
import { IOSRocketIcon } from "../../../atoms/icons/IOSRocketIcon";
import { XIcon } from "../../../atoms/icons/XIcon";
import { ConfirmModal } from "../../../templates";

const kisiSignatureKeyAtom = atom<string>();
export const KisiIntegrationUpdateModal = () => {
  const { hide } = useContext(ModalContext);
  const { t } = useTranslation();
  const [signatureKey, setSignatureKey] = useAtom(kisiSignatureKeyAtom);
  const [tabId, setTabId] = useState<"access" | "membership" | "booking">(
    "access",
  );
  const tabs = useMemo(
    () => [
      {
        id: "access" as const,
        label: t("components.kisiIntegrationUpdateModal.tabs.access.label"),
        title: t("components.kisiIntegrationUpdateModal.tabs.access.title"),
        description: t(
          "components.kisiIntegrationUpdateModal.tabs.access.description",
        ),
      },
      {
        id: "membership" as const,
        label: t("components.kisiIntegrationUpdateModal.tabs.membership.label"),
        title: t("components.kisiIntegrationUpdateModal.tabs.membership.title"),
        description: t(
          "components.kisiIntegrationUpdateModal.tabs.membership.description",
        ),
      },
      {
        id: "booking" as const,
        label: t("components.kisiIntegrationUpdateModal.tabs.booking.label"),
        title: t("components.kisiIntegrationUpdateModal.tabs.booking.title"),
        description: t(
          "components.kisiIntegrationUpdateModal.tabs.booking.description",
        ),
      },
    ],
    [t],
  );
  const currentTab = tabs.find((tab) => tab.id === tabId);

  const { api } = useGymflowModels();
  const { data: dataBrandConfig } = useQueryKisiBrandConfig({
    api,
  });

  const generateSignatureKey = useMutationKisiGenerateSignatureKey({
    api,
  });
  const articleLinks = {
    access:
      "https://support.gymflow.io/en/help/articles/9250140-integrate-kisi-with-access-logs",
    membership:
      "https://support.gymflow.io/en/help/articles/3533545-grant-access-for-memberships",
    booking:
      "https://support.gymflow.io/en/help/articles/3116834-grant-access-for-bookings",
  } as const;
  const { stackModal, popModal } = useContext(ModalContext);
  return (
    <ModalWrapper
      className="flex max-h-[90vh] w-full max-w-xl"
      onCancel={() => hide()}
    >
      <div className="flex w-full flex-col gap-6 overflow-y-auto">
        <div className="flex flex-row justify-between">
          <div className="bg-secondary-50 flex size-11 items-center justify-center rounded-full">
            <div className="bg-secondary-100 flex size-8 items-center justify-center self-center rounded-full">
              <DangerIcon
                className="size-5"
                pathClassName="stroke-secondary-600"
              />
            </div>
          </div>
          <div className="cursor-pointer" onClick={() => hide()}>
            <XIcon className="size-6" pathClassName="stroke-gray-600" />
          </div>
        </div>
        <div className="relative flex flex-col gap-5">
          <div className="flex flex-col">
            <div className="text-lg font-semibold text-gray-950">
              {t("components.kisiIntegrationUpdateModal.title")}
            </div>
            <div className="text-sm font-medium text-gray-500">
              {t("components.kisiIntegrationUpdateModal.description")}
            </div>
          </div>
          <PillTabs tabs={tabs} tabId={tabId} setTabId={setTabId} />

          <AnimatePresence mode="wait">
            <motion.div
              key={tabId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="shadow-xs flex flex-col gap-3 rounded-xl border border-gray-200 p-3"
            >
              <div className="flex flex-row gap-0.5 text-base font-semibold text-gray-950">
                {currentTab?.title}
                <IOSRocketIcon className="size-6" />
              </div>

              <div className="flex flex-col gap-1 whitespace-pre-line text-sm font-medium text-gray-500">
                {currentTab?.description}
              </div>
              <div className="gap-.5 flex w-full flex-col rounded-[.625rem] bg-gray-100 px-3 py-2">
                <div className="flex flex-row items-center gap-2">
                  <InfoCircleIcon className="size-4 text-gray-500" />
                  <div className="text-sm font-medium text-gray-950">
                    {t("components.kisiIntegrationUpdateModal.guide")}
                  </div>
                </div>
                <div className="flex flex-row items-center justify-end">
                  <Button
                    href={articleLinks[tabId]}
                    target="_blank"
                    intent="link-secondary"
                    className="h-fit p-0 text-sm"
                  >
                    {t("components.kisiIntegrationUpdateModal.openArticle")}
                    <ArrowUpRightIcon className="text-secondary-500 size-4" />
                  </Button>
                </div>
              </div>
            </motion.div>

            {tabId === "access" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="flex flex-row gap-3 max-lg:!flex-col"
              >
                <CopyInput
                  label={t("components.kisiIntegrationUpdateModal.webhookUrl")}
                  className="w-[calc(50%-.75rem/2)] max-lg:w-full"
                  copyInfo={dataBrandConfig?.webhookUrl ?? ""}
                />
                <CopyInput
                  label={t(
                    "components.kisiIntegrationUpdateModal.signatureKey",
                  )}
                  className="w-[calc(50%-.75rem/2)] max-lg:w-full"
                  copyInfo={signatureKey ?? "•".repeat(12)}
                  buttons={(onCopy) => (
                    <>
                      {signatureKey && (
                        <ReactiveButton
                          size="small"
                          className="aspect-square p-0"
                          onClick={() => onCopy()}
                          reactiveContent={
                            <CheckIcon pathClassName="stroke-gray-950" />
                          }
                        >
                          <CopyIcon
                            className="size-5"
                            pathClassName="stroke-gray-950"
                          />
                        </ReactiveButton>
                      )}
                      <Button
                        size="small"
                        className="aspect-square p-0"
                        onClick={async () => {
                          stackModal(
                            <ConfirmModal
                              title={t(
                                "components.kisiIntegrationUpdateModal.generateNewKey.title",
                              )}
                              onConfirm={async () => {
                                const key =
                                  await generateSignatureKey.mutateAsync();
                                setSignatureKey(key.secretKey);
                                popModal();
                              }}
                              onCancel={() => {
                                popModal();
                              }}
                              icon={
                                <RefreshIcon
                                  className="size-5"
                                  pathClassName="stroke-secondary-700"
                                />
                              }
                            >
                              {t(
                                "components.kisiIntegrationUpdateModal.generateNewKey.description",
                              )}
                            </ConfirmModal>,
                          );
                        }}
                      >
                        <RefreshIcon
                          className="size-5"
                          pathClassName="stroke-gray-950"
                        />
                      </Button>
                    </>
                  )}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="flex flex-col items-center justify-center gap-4">
          <Button intent="secondary" className="!px-14" onClick={() => hide()}>
            {t("portal.common.gotIt")}
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};
